import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Box, Container, Grid, Stack, SvgIcon, Typography } from '@mui/material'

import { Meta } from '@/components/base'
import Button from '@/components/base/button'
import Layout from '@/components/layout/layout'
import { SearchCard } from '@/components/sections/search-grid'
import Spacer from '@/components/sections/spacer'
import { useAppStore } from '@/contexts/store'
import { useLocaleStore } from '@/store/locale'
import { useWishlistStore } from '@/store/wishlist'
import { formatPrice } from '@/utils/strings'

import { useAdvertisementsSearch } from '@/api/advertisements'
import { WPImage, WPLink } from '@/types'

export default function WishlistPage() {
  const { strings } = useAppStore()
  const { currentLocale } = useLocaleStore()
  const { items, removeItem, clearWishlist } = useWishlistStore()
  const [mounted, setMounted] = useState(false)

  // Handle hydration mismatch
  useEffect(() => {
    setMounted(true)
  }, [])

  // Get IDs from wishlist
  const wishlistIds = useMemo(() => items.map((item) => item.id), [items])

  // Fetch advertisements by IDs
  const { collection, isLoading } = useAdvertisementsSearch(
    mounted && wishlistIds.length > 0 ? { ids: wishlistIds } : undefined
  )

  // Don't render anything until mounted (prevent hydration mismatch)
  if (!mounted) {
    return (
      <>
        <Meta
          pageProps={{
            yoast_head: `<title>${strings?.labels?.wishlist || ''}</title>`,
            meta_robots: {
              is_robots_noindex: true,
              is_robots_nofollow: true
            }
          }}
        />
        <Spacer size="xl" />
        <Container>
          <Grid container>
            <Grid size={{ xs: 4, sm: 8, md: 12 }}>
              <Typography variant="h2" color="text.secondaryContrastText">
                {strings?.labels?.my_wishlist || strings?.labels?.wishlist || ''}
              </Typography>
            </Grid>
          </Grid>
        </Container>
        <Spacer size="xl" />
      </>
    )
  }

  return (
    <>
      <Meta
        pageProps={{
          yoast_head: `<title>${strings?.labels?.wishlist || ''}</title>`,
          meta_robots: {
            is_robots_noindex: true,
            is_robots_nofollow: true
          }
        }}
      />

      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 8, md: 12 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              gap={2}
            >
              <Typography variant="h2" color="text.secondaryContrastText">
                {strings?.labels?.my_wishlist || strings?.labels?.wishlist || ''}
              </Typography>

              {items.length > 0 && (
                <Button variant="text" icon="delete" onClick={() => clearWishlist()}>
                  {strings?.labels.clear_wishlist}
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <Spacer size="lg" />

      <Container>
        <Grid container spacing={3}>
          {isLoading ? (
            <Grid size={{ xs: 4, sm: 8, md: 12 }}>
              <Typography variant="textLarge" color="text.secondary">
                {strings?.labels.loading}
              </Typography>
            </Grid>
          ) : items.length === 0 ? (
            <Grid size={{ xs: 4, sm: 8, md: 12 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 4,
                  bgcolor: 'grey.50',
                  borderRadius: (theme) => theme.shape.borderRadius4
                }}
              >
                <SvgIcon sx={{ fontSize: '5rem', color: 'grey.300', mb: 2 }}>
                  <use href="#ico-heart" />
                </SvgIcon>
                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                  {strings?.labels.empty_wishlist}
                </Typography>
                <Typography variant="textRegular" color="text.secondary" sx={{ mb: 3 }}>
                  {strings?.labels.empty_wishlist_description}
                </Typography>
                {strings?.links?.browse_ads?.url ? (
                  <Button href={strings.links.browse_ads.url} variant="contained" icon="search">
                    {strings.links.browse_ads.title}
                  </Button>
                ) : null}
              </Box>
            </Grid>
          ) : (
            collection?.map((model) => (
              <Grid key={model.id} size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                <SearchCard
                  skeleton={false}
                  pretitle={model.property.city?.name?.[currentLocale!] || ''}
                  title={model.title?.[currentLocale!] || ''}
                  subtitle={`${formatPrice(model.price)}/${strings?.labels?.month}`}
                  text={model.description?.[currentLocale!] || ''}
                  chips={model.features.map((f) => ({ label: f.name?.[currentLocale!] || '' }))}
                  image={
                    model.attachments?.[0]
                      ? ({
                          url: `/images/${model.attachments[0].path}`,
                          alt: model.title?.[currentLocale!] || ''
                        } as WPImage)
                      : undefined
                  }
                  link={
                    {
                      url: `/advertisements/${model.id}`,
                      title: model.title?.[currentLocale!] || '',
                      target: ''
                    } as WPLink
                  }
                  wishlist={{
                    advertisementId: model.id,
                    isInWishlist: true,
                    onToggle: () => removeItem(model.id)
                  }}
                />
              </Grid>
            ))
          )}
        </Grid>
      </Container>

      <Spacer size="2xl" />
    </>
  )
}

WishlistPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>
}

export async function getStaticProps() {
  return {
    props: {}
  }
}
